"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Link } from "@/components/common/Link";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map(item => {
        if (item.label === "Public Services" && item.children && item.children.length > 0) {
          return <NavigationMenuItem key={item.id}>
                <NavigationMenuTrigger className="text-foreground font-default uppercase tracking-wider text-sm">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="w-48 p-2 bg-card border border-border shadow-lg">
                    {item.children.map(child => <NavigationMenuLink key={child.id} asChild>
                        <Link to={child.href || "#"} className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors font-default">
                          {child.label}
                        </Link>
                      </NavigationMenuLink>)}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>;
        }
        return <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-foreground hover:text-secondary transition-colors font-default uppercase tracking-wider text-sm">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-4">
      {menuItems.map(item => {
      if (item.children && item.children.length > 0) {
        return <div key={item.id}>
              <div className="font-default font-bold text-foreground uppercase tracking-wide">{item.label}</div>
              <div className="ml-4 flex flex-col gap-2">
                {item.children.map(child => <Link key={child.id} to={child.href || "#"} className="text-foreground hover:text-secondary transition-colors font-default">
                    {child.label}
                  </Link>)}
              </div>
            </div>;
      }
      return <Link key={item.id} to={item.href || "#"} className="text-foreground hover:text-secondary transition-colors font-default uppercase tracking-wide">
            {item.label}
          </Link>;
    })}
      <Link to="tel:911">
        <Button variant="destructive" className="w-full font-default font-bold">
          <Phone className="w-4 h-4" />
          EMERGENCY: 911
        </Button>
      </Link>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265103" id="header" className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/10" : "bg-background border-b border-primary/10"}`}>
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0">
          <WvcLogo className="h-10 w-auto text-primary" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 mx-8">
          <MenuProvider menu_id="37">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-6">
          <Link to="tel:911" className="flex items-center gap-2 text-destructive hover:opacity-80 transition-opacity">
            <Phone className="w-5 h-5" />
            <span className="font-default font-extrabold tracking-tighter">EMERGENCY: 911</span>
          </Link>
          <Link to="/ContactPage">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold rounded-md shadow-sm">
              File a Report
            </Button>
          </Link>
        </div>

        {}
        <div className="md:hidden flex items-center gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-primary">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 bg-background border-l border-border">
              <div className="mt-8">
                <MenuProvider menu_id="37">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}